import api from './api.service';

function unwrap<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return ((data as { data?: T })?.data ?? data) as T;
}

export interface Supplier360Document {
  id: string;
  document_name?: string;
  document_title?: string;
  file_name?: string;
  document_type?: string;
  document_category?: string;
  document_status?: string;
  file_size_bytes?: number;
  created_at?: string;
  entity_type?: string;
  entity_id?: string;
}

export interface Supplier360Assessment {
  id: string;
  assessment_number?: string;
  assessment_type?: string;
  assessment_date?: string;
  status?: string;
  workflow_stage?: string;
  total_score?: number;
  max_possible_score?: number;
  percentage_score?: number;
  pass_fail_result?: string;
  recommendation?: string;
  next_assessment_date?: string;
  evaluator_comments?: string;
  approved_at?: string;
  template_name?: string;
  template_category?: string;
  template_passing_score?: number;
}

export interface Supplier360Evidence {
  id: string;
  assessment_id: string;
  evidence_url?: string;
  evidence_type?: string;
  evidence_description?: string;
  response_value?: string;
  comments?: string;
  question_text?: string;
  section?: string;
}

export interface Supplier360Summary {
  po_count: number;
  open_po_count: number;
  open_po_value: number;
  grn_count: number;
  invoice_count: number;
  open_ap_balance: number;
  advance_count: number;
  assessment_count: number;
  latest_assessment: {
    id: string;
    assessment_number?: string;
    status?: string;
    pass_fail_result?: string;
    percentage_score?: number;
    assessment_date?: string;
  } | null;
  supplier_document_count: number;
  assessment_document_count: number;
  blacklist_active: boolean;
}

export interface Supplier360Workspace {
  supplier: Record<string, unknown>;
  summary?: Supplier360Summary;
  commercial: { purchase_orders: unknown[]; inquiries: unknown[] };
  operational: { grns: unknown[] };
  financial: { invoices: unknown[]; advances: unknown[] };
  quality: {
    assessments: Supplier360Assessment[];
    blacklist: unknown[];
    assessment_documents?: Supplier360Document[];
    assessment_evidence?: Supplier360Evidence[];
  };
  documents?: {
    supplier: Supplier360Document[];
    assessments: Supplier360Document[];
  };
  intelligence: {
    performance: unknown;
    risk_profile: unknown;
    score: unknown;
    development_plans: unknown[];
    decision_checkpoint: DecisionCheckpoint | null;
  };
}

export interface DecisionCheckpoint {
  checks: Array<{ key: string; label: string; pass: boolean; detail: string; requires_executive?: boolean }>;
  ready_to_expand: boolean;
  audit_due: boolean;
  composite_score?: number;
  segment?: string;
}

function apiErrorMessage(e: unknown, fallback: string): string {
  const ax = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.response?.data?.error || ax?.message || fallback;
}

export async function getSupplier360(supplierId: string): Promise<Supplier360Workspace> {
  try {
    const res = await api.get(`/procurement/suppliers/${supplierId}/360`);
    return unwrap<Supplier360Workspace>(res);
  } catch (e) {
    throw new Error(apiErrorMessage(e, 'Failed to load supplier 360'));
  }
}

export async function calculateEnterpriseScore(supplierId: string, body?: { period_start?: string; period_end?: string }) {
  try {
    const res = await api.post(`/procurement/suppliers/${supplierId}/calculate-enterprise-score`, body ?? {});
    return unwrap(res);
  } catch (e) {
    throw new Error(apiErrorMessage(e, 'Score calculation failed'));
  }
}

export async function getDecisionCheckpoint(supplierId: string): Promise<DecisionCheckpoint> {
  const res = await api.get(`/procurement/suppliers/${supplierId}/decision-checkpoint`);
  return unwrap<DecisionCheckpoint>(res);
}

export async function validateSupplierForPo(supplierId: string): Promise<{ allowed: boolean; warning?: string }> {
  const res = await api.get(`/procurement/suppliers/${supplierId}/po-validation`);
  return unwrap(res);
}

export async function approveSupplier(supplierId: string) {
  const res = await api.post(`/procurement/suppliers/${supplierId}/approve`);
  return unwrap(res);
}

export async function getControlTowerSummary() {
  const res = await api.get('/procurement/control-tower/summary');
  return unwrap(res);
}

export async function refreshControlTowerScores() {
  const res = await api.post('/procurement/control-tower/refresh-scores');
  return unwrap(res);
}

export async function blacklistSupplier(supplierId: string, body: Record<string, unknown>) {
  const res = await api.post(`/procurement/suppliers/${supplierId}/blacklist`, body);
  return unwrap(res);
}

export async function approveBlacklist(blacklistId: string, comments?: string) {
  const res = await api.post(`/procurement/blacklist/${blacklistId}/approve`, { comments });
  return unwrap(res);
}

export async function createDevelopmentPlan(supplierId: string, body: Record<string, unknown>) {
  try {
    const res = await api.post(`/procurement/suppliers/${supplierId}/development-plans`, body);
    return unwrap(res);
  } catch (e) {
    throw new Error(apiErrorMessage(e, 'Failed to create plan'));
  }
}

export async function getPublicSupplierPo(token: string) {
  const res = await api.get(`/procurement/public/supplier-po/${token}`);
  return unwrap(res);
}

export async function confirmSupplierPoDelivery(token: string, body: { confirmed_delivery_date: string; supplier_notes?: string }) {
  const res = await api.post(`/procurement/public/supplier-po/${token}/confirm-delivery`, body);
  return unwrap(res);
}

export async function createPoPortalLink(poId: string, supplierId: string) {
  const res = await api.post(`/procurement/purchase-orders/${poId}/portal-link`, { supplier_id: supplierId });
  return unwrap<{ token: string; portal_path: string }>(res);
}
